import { Link } from "gatsby"
import React, { useEffect, useState } from "react"
import { FaBars, FaTimes } from "react-icons/fa"
import serviceList from "../../data/serviceList"
import "./navbar.css"

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [showServices, setShowServices] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }

    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false)
        setShowServices(false)
      }
    }

    window.addEventListener("scroll", handleScroll)
    window.addEventListener("resize", handleResize)
    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about/", label: "About Us" },
    { path: "/services/", label: "Services" },
    { path: "/blog/", label: "Blog" },
    { path: "/gallery/", label: "Gallery" },
    { path: "/contact/", label: "Contact Us" },
    { path: "/quote/", label: "Request Quote" },
  ]

  // Edit services (incl. comingSoon) in src/data/serviceList.js
  const individualServices = serviceList.map(s => ({
    path: `/services/${s.slug}/`,
    label: s.shortTitle || s.title,
    comingSoon: s.comingSoon,
  }))

  return (
    <header
      className={`navbar transition-all duration-300 ${
        isScrolled
          ? "bg-white shadow-md text-black"
          : "bg-transparent text-white has-gradient"
      } fixed top-0 left-0 right-0 z-10 h-20`}
    >
      <div className="relative flex items-center h-full px-6 w-full lg:px-12">
        <div className="flex items-center flex-grow-0 h-full">
          <Link to="/" className="flex items-center h-full">
            <img
              src={`${
                isScrolled ? "/images/fullogo.png" : "/images/fullogowhite.png"
              }`}
              alt="BT Auto Care logo"
              width={86}
              height={40}
              className="max-h-10 m-auto w-auto transition-all duration-300"
            />
          </Link>
        </div>

        <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 space-x-6 items-center">
          {navLinks
            .filter(link => link.label !== "Request Quote")
            .map(link =>
              link.label === "Services" ? (
                <div className="dropdown relative" key={link.path}>
                  <Link
                    to={link.path}
                    className="link block py-2 lg:inline-block lg:py-0"
                    activeClassName="active"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Services
                  </Link>
                  <div
                    className={`dropdown-menu ${
                      isScrolled ? "bg-scrolled" : "bg-blur"
                    }`}
                  >
                    {individualServices.map(service =>
                      service.comingSoon ? (
                        <span
                          key={service.path}
                          className="block px-4 py-2 text-gray-400 cursor-not-allowed opacity-60"
                        >
                          {service.label}
                          <div className="text-yellow-400 text-xs uppercase tracking-wide mt-1 coming-soon-label">
                            Coming Soon
                          </div>
                        </span>
                      ) : (
                        <Link
                          key={service.path}
                          to={service.path}
                          onClick={() => setIsMenuOpen(false)}
                          className="block px-4 py-2"
                        >
                          {service.label}
                        </Link>
                      )
                    )}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className="link block py-2 lg:inline-block lg:py-0"
                  activeClassName="active"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              )
            )}
        </div>

        <div className="hidden lg:block ml-auto">
          <Link
            to="/quote/"
            className={`quote-btn no-underline ${isScrolled ? "scrolled" : ""}`}
          >
            Request Quote
          </Link>
        </div>

        <div className="lg:hidden ml-auto">
          <button
            className="focus:outline-none text-2xl"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        <div className="lg:hidden absolute right-16 top-6">
          <Link
            to="/quote/"
            className={`quote-btn no-underline ${isScrolled ? "scrolled" : ""}`}
          >
            Request Quote
          </Link>
        </div>

        <>
          {isMenuOpen && (
            <>
              <div
                className="fixed inset-0 bg-black bg-opacity-30 z-40"
                onClick={() => setIsMenuOpen(false)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    setIsMenuOpen(false)
                  }
                }}
                aria-label="Close menu"
              />

              <nav className="mobile-menu-drawer lg:hidden fixed top-0 right-0 h-full w-72 bg-black z-50 p-6 pt-20 shadow-lg text-white">
                <button
                  className="absolute top-5 right-5 text-white text-2xl"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FaTimes />
                </button>

                {navLinks
                  .filter(link => link.label !== "Request Quote")
                  .map(link =>
                    link.label === "Services" ? (
                      <div
                        key={link.path}
                        className="mobile-dropdown space-y-2"
                      >
                        <div className="w-full">
                          <div className="flex justify-between items-center py-2">
                            <Link
                              to="/services/"
                              className="text-left text-white font-semibold no-underline-mobile"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              Services
                            </Link>

                            <button
                              onClick={() => setShowServices(!showServices)}
                              className="text-xl text-white focus:outline-none"
                              aria-label="Toggle Services Dropdown"
                            >
                              {showServices ? "−" : "+"}
                            </button>
                          </div>
                          <div className="border-b border-white/20" />
                        </div>

                        {showServices && (
                          <div className="mobile-dropdown-menu">
                            {individualServices.map(service =>
                              service.comingSoon ? (
                                <span
                                  key={service.path}
                                  className="flex justify-between items-center"
                                >
                                  <span>{service.label}</span>
                                  <span className="coming-soon-label text-yellow-400 text-xs uppercase ml-2">
                                    Coming Soon
                                  </span>
                                </span>
                              ) : (
                                <Link
                                  key={service.path}
                                  to={service.path}
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  {service.label}
                                </Link>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="link block py-2 text-white"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {link.label}
                      </Link>
                    )
                  )}

                <Link
                  to="/quote/"
                  className="quote-btn mt-8"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Request Quote
                </Link>
              </nav>
            </>
          )}
        </>
      </div>
    </header>
  )
}

export default Navbar
